/** XeAgents.ai**/
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Loader2, Save, Shield, ShieldCheck, Timer, Clock, Webhook, CreditCard, Bell, Key, RefreshCw, Phone, Wrench, Eye, Sparkles } from "lucide-react";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SystemSettingsData {
  jwt_expiry_days: number;
  otp_expiry_minutes: number;
  password_reset_expiry_minutes: number;
  login_mfa_enabled: boolean;
  login_mfa_expiry_minutes: number;
  ai_b2b_leads_menu_enabled: boolean;
  phone_number_monthly_credits: number;
  low_credits_threshold: number;
  webhook_retry_max_attempts: number;
  webhook_retry_intervals_minutes: number[];
  webhook_expiry_hours: number;
  system_phone_pool_size: number;
}

const DEFAULT_SETTINGS: SystemSettingsData = {
  jwt_expiry_days: 7,
  otp_expiry_minutes: 5,
  password_reset_expiry_minutes: 5,
  login_mfa_enabled: true,
  login_mfa_expiry_minutes: 10,
  ai_b2b_leads_menu_enabled: true,
  phone_number_monthly_credits: 50,
  low_credits_threshold: 50,
  webhook_retry_max_attempts: 5,
  webhook_retry_intervals_minutes: [1, 5, 15, 30, 60],
  webhook_expiry_hours: 24,
  system_phone_pool_size: 5,
};

export default function SystemSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState<SystemSettingsData>(DEFAULT_SETTINGS);
  const [webhookIntervalsStr, setWebhookIntervalsStr] = useState("1, 5, 15, 30, 60");
  const [hasChanges, setHasChanges] = useState(false);
  const [syncingAllCalls, setSyncingAllCalls] = useState(false);
  const [syncingWebhooks, setSyncingWebhooks] = useState(false);
  const [confirmDisableMfa, setConfirmDisableMfa] = useState(false);

  const { data: settings, isLoading, refetch } = useQuery({
    queryKey: ["/api/admin/system-settings"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/system-settings");
      if (!response.ok) {
        throw new Error("Failed to fetch system settings");
      }
      return response.json();
    },
  });

  useEffect(() => {
    if (settings) {
      const newFormData = {
        jwt_expiry_days: settings.jwt_expiry_days ?? DEFAULT_SETTINGS.jwt_expiry_days,
        otp_expiry_minutes: settings.otp_expiry_minutes ?? DEFAULT_SETTINGS.otp_expiry_minutes,
        password_reset_expiry_minutes: settings.password_reset_expiry_minutes ?? DEFAULT_SETTINGS.password_reset_expiry_minutes,
        login_mfa_enabled: settings.login_mfa_enabled ?? DEFAULT_SETTINGS.login_mfa_enabled,
        login_mfa_expiry_minutes: settings.login_mfa_expiry_minutes ?? DEFAULT_SETTINGS.login_mfa_expiry_minutes,
        ai_b2b_leads_menu_enabled: settings.ai_b2b_leads_menu_enabled ?? DEFAULT_SETTINGS.ai_b2b_leads_menu_enabled,
        phone_number_monthly_credits: settings.phone_number_monthly_credits ?? DEFAULT_SETTINGS.phone_number_monthly_credits,
        low_credits_threshold: settings.low_credits_threshold ?? DEFAULT_SETTINGS.low_credits_threshold,
        webhook_retry_max_attempts: settings.webhook_retry_max_attempts ?? DEFAULT_SETTINGS.webhook_retry_max_attempts,
        webhook_retry_intervals_minutes: settings.webhook_retry_intervals_minutes ?? DEFAULT_SETTINGS.webhook_retry_intervals_minutes,
        webhook_expiry_hours: settings.webhook_expiry_hours ?? DEFAULT_SETTINGS.webhook_expiry_hours,
        system_phone_pool_size: settings.system_phone_pool_size ?? DEFAULT_SETTINGS.system_phone_pool_size,
      };
      setFormData(newFormData);
      setWebhookIntervalsStr(newFormData.webhook_retry_intervals_minutes.join(", "));
    }
  }, [settings]);

  const updateSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const response = await apiRequest("PATCH", `/api/admin/settings/${key}`, { value });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update setting");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/system-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSaveAll = async () => {
    try {
      const intervals = webhookIntervalsStr.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n));
      
      const settingsToUpdate = [
        { key: "jwt_expiry_days", value: formData.jwt_expiry_days },
        { key: "otp_expiry_minutes", value: formData.otp_expiry_minutes },
        { key: "password_reset_expiry_minutes", value: formData.password_reset_expiry_minutes },
        { key: "login_mfa_enabled", value: formData.login_mfa_enabled },
        { key: "login_mfa_expiry_minutes", value: formData.login_mfa_expiry_minutes },
        { key: "ai_b2b_leads_menu_enabled", value: formData.ai_b2b_leads_menu_enabled },
        { key: "phone_number_monthly_credits", value: formData.phone_number_monthly_credits },
        { key: "low_credits_threshold", value: formData.low_credits_threshold },
        { key: "webhook_retry_max_attempts", value: formData.webhook_retry_max_attempts },
        { key: "webhook_retry_intervals_minutes", value: intervals },
        { key: "webhook_expiry_hours", value: formData.webhook_expiry_hours },
        { key: "system_phone_pool_size", value: formData.system_phone_pool_size },
      ];

      for (const setting of settingsToUpdate) {
        await updateSettingMutation.mutateAsync(setting);
      }

      await refetch();
      setHasChanges(false);

      toast({
        title: "Settings Saved",
        description: "All system settings have been updated successfully.",
      });
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  const handleInputChange = (key: keyof SystemSettingsData, value: number | string) => {
    setHasChanges(true);
    if (key === "webhook_retry_intervals_minutes") {
      setWebhookIntervalsStr(value as string);
    } else {
      setFormData(prev => ({
        ...prev,
        [key]: typeof value === "string" ? parseInt(value) || 0 : value,
      }));
    }
  };

  // Turning the code requirement OFF weakens login for everyone, so it needs an explicit confirmation.
  // Turning it back on needs none. Like every field on this page it only takes effect on Save.
  const handleMfaToggle = (checked: boolean) => {
    if (checked) {
      setHasChanges(true);
      setFormData(prev => ({ ...prev, login_mfa_enabled: true }));
    } else {
      setConfirmDisableMfa(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">System Configuration</h3>
          <p className="text-sm text-muted-foreground">
            Configure security, credits, and webhook settings for the platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            data-testid="button-refresh-settings"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleSaveAll}
            disabled={updateSettingMutation.isPending || !hasChanges}
            data-testid="button-save-all-settings"
          >
            {updateSettingMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save All Changes
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Security Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-4 w-4 text-primary" />
              Security Settings
            </CardTitle>
            <CardDescription>
              Authentication and security token expiration times
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <Label htmlFor="login_mfa_enabled" className="flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                  Email Code at Login (MFA)
                  <InfoTooltip content="Every sign-in requires a one-time code emailed to the user. People who are already signed in are not asked again until they sign in anew." />
                </Label>
                <div className="flex items-center gap-2">
                  <Badge variant={formData.login_mfa_enabled ? "default" : "destructive"} data-testid="badge-login-mfa-status">
                    {formData.login_mfa_enabled ? "On" : "Off"}
                  </Badge>
                  <Switch
                    id="login_mfa_enabled"
                    checked={formData.login_mfa_enabled}
                    onCheckedChange={handleMfaToggle}
                    data-testid="switch-login-mfa"
                  />
                </div>
              </div>
              {!formData.login_mfa_enabled && (
                <p className="text-xs text-destructive" data-testid="text-login-mfa-warning">
                  Off: anyone with a valid password can sign in without an emailed code.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="login_mfa_expiry_minutes" className="flex items-center gap-2">
                <Timer className="h-3.5 w-3.5 text-muted-foreground" />
                Login Code Expiry
                <InfoTooltip content="How long an emailed sign-in code stays valid (5-30 minutes)" />
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="login_mfa_expiry_minutes"
                  type="number"
                  min={5}
                  max={30}
                  value={formData.login_mfa_expiry_minutes}
                  onChange={(e) => handleInputChange("login_mfa_expiry_minutes", e.target.value)}
                  className="w-24"
                  disabled={!formData.login_mfa_enabled}
                  data-testid="input-login-mfa-expiry"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="jwt_expiry_days" className="flex items-center gap-2">
                <Key className="h-3.5 w-3.5 text-muted-foreground" />
                JWT Token Expiry
                <InfoTooltip content="How long users stay logged in before needing to sign in again" />
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="jwt_expiry_days"
                  type="number"
                  min={1}
                  max={90}
                  value={formData.jwt_expiry_days}
                  onChange={(e) => handleInputChange("jwt_expiry_days", e.target.value)}
                  className="w-24"
                  data-testid="input-jwt-expiry"
                />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="otp_expiry_minutes" className="flex items-center gap-2">
                <Timer className="h-3.5 w-3.5 text-muted-foreground" />
                OTP Verification Expiry
                <InfoTooltip content="Time before email verification codes expire" />
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="otp_expiry_minutes"
                  type="number"
                  min={1}
                  max={60}
                  value={formData.otp_expiry_minutes}
                  onChange={(e) => handleInputChange("otp_expiry_minutes", e.target.value)}
                  className="w-24"
                  data-testid="input-otp-expiry"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="password_reset_expiry" className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                Password Reset Expiry
                <InfoTooltip content="Time before password reset codes expire" />
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="password_reset_expiry"
                  type="number"
                  min={1}
                  max={60}
                  value={formData.password_reset_expiry_minutes}
                  onChange={(e) => handleInputChange("password_reset_expiry_minutes", e.target.value)}
                  className="w-24"
                  data-testid="input-password-reset-expiry"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credits Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="h-4 w-4 text-primary" />
              Credits & Billing
            </CardTitle>
            <CardDescription>
              Phone billing and credit notification thresholds
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone_monthly_credits" className="flex items-center gap-2">
                Phone Number Monthly Cost
                <InfoTooltip content="Credits deducted monthly for each rented phone number" />
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="phone_monthly_credits"
                  type="number"
                  min={0}
                  max={1000}
                  value={formData.phone_number_monthly_credits}
                  onChange={(e) => handleInputChange("phone_number_monthly_credits", e.target.value)}
                  className="w-24"
                  data-testid="input-phone-monthly-credits"
                />
                <span className="text-sm text-muted-foreground">credits/month</span>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="low_credits_threshold" className="flex items-center gap-2">
                <Bell className="h-3.5 w-3.5 text-muted-foreground" />
                Low Credits Alert Threshold
                <InfoTooltip content="Send notification email when user credits fall below this amount" />
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="low_credits_threshold"
                  type="number"
                  min={0}
                  max={500}
                  value={formData.low_credits_threshold}
                  onChange={(e) => handleInputChange("low_credits_threshold", e.target.value)}
                  className="w-24"
                  data-testid="input-low-credits-threshold"
                />
                <span className="text-sm text-muted-foreground">credits</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Webhook Settings */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Webhook className="h-4 w-4 text-primary" />
              Webhook Retry Settings
            </CardTitle>
            <CardDescription>
              Configure automatic retry behavior for failed webhook deliveries
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="webhook_max_attempts" className="flex items-center gap-2">
                  Max Retry Attempts
                  <InfoTooltip content="Maximum number of times to retry a failed webhook" />
                </Label>
                <Input
                  id="webhook_max_attempts"
                  type="number"
                  min={1}
                  max={10}
                  value={formData.webhook_retry_max_attempts}
                  onChange={(e) => handleInputChange("webhook_retry_max_attempts", e.target.value)}
                  className="w-full"
                  data-testid="input-webhook-max-attempts"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhook_expiry" className="flex items-center gap-2">
                  Webhook Expiry
                  <InfoTooltip content="Hours before giving up on retrying a failed webhook" />
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="webhook_expiry"
                    type="number"
                    min={1}
                    max={168}
                    value={formData.webhook_expiry_hours}
                    onChange={(e) => handleInputChange("webhook_expiry_hours", e.target.value)}
                    data-testid="input-webhook-expiry"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">hours</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhook_intervals" className="flex items-center gap-2">
                  Retry Intervals (minutes)
                  <InfoTooltip content="Comma-separated list of wait times between retry attempts" />
                </Label>
                <Input
                  id="webhook_intervals"
                  type="text"
                  value={webhookIntervalsStr}
                  onChange={(e) => handleInputChange("webhook_retry_intervals_minutes", e.target.value)}
                  placeholder="1, 5, 15, 30, 60"
                  data-testid="input-webhook-intervals"
                />
              </div>
            </div>

            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <div className="text-xs text-muted-foreground">
                <strong>Current Configuration:</strong> Failed webhooks will retry up to{" "}
                <Badge variant="secondary" className="mx-1">{formData.webhook_retry_max_attempts}</Badge>
                times with intervals of{" "}
                <Badge variant="secondary" className="mx-1">{webhookIntervalsStr}</Badge>
                minutes. Webhooks expire after{" "}
                <Badge variant="secondary" className="mx-1">{formData.webhook_expiry_hours}</Badge>
                hours.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Resources */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Phone className="h-4 w-4 text-primary" />
              {t("admin.settings.resources.title") || "System Resources"}
            </CardTitle>
            <CardDescription>
              {t("admin.settings.resources.description") || "Configure system resource pools and limits"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="system_phone_pool_size" className="flex items-center gap-2">
                {t("admin.settings.resources.poolSize") || "System Phone Pool Size"}
                <InfoTooltip content={t("admin.settings.resources.poolSizeTooltip") || "Number of shared phone numbers available in the system pool for Free tier users"} />
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="system_phone_pool_size"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.system_phone_pool_size}
                  onChange={(e) => handleInputChange("system_phone_pool_size", e.target.value)}
                  className="w-24"
                  data-testid="input-pool-size"
                />
                <span className="text-sm text-muted-foreground">phones</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("admin.settings.resources.poolSizeHint") || "Shared phone numbers for users on Free plan"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Menu Visibility */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Eye className="h-4 w-4 text-primary" />
              Menu Visibility
            </CardTitle>
            <CardDescription>
              Show or hide optional sections of the app for everyone
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <Label htmlFor="ai_b2b_leads_menu_enabled" className="flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
                  Lead Lists
                  <InfoTooltip content="Shows the Lead Lists entry in the sidebar and opens its page: import a lead CSV, filter it, and push the result into a campaign. Off hides both, so the section stays out of sight during a demo or a screen share. Saved leads are kept either way." />
                </Label>
                <div className="flex items-center gap-2">
                  <Badge variant={formData.ai_b2b_leads_menu_enabled ? "default" : "secondary"} data-testid="badge-ai-b2b-leads-status">
                    {formData.ai_b2b_leads_menu_enabled ? "Shown" : "Hidden"}
                  </Badge>
                  <Switch
                    id="ai_b2b_leads_menu_enabled"
                    checked={formData.ai_b2b_leads_menu_enabled}
                    onCheckedChange={(checked) => {
                      setHasChanges(true);
                      setFormData(prev => ({ ...prev, ai_b2b_leads_menu_enabled: checked }));
                    }}
                    data-testid="switch-ai-b2b-leads"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {formData.ai_b2b_leads_menu_enabled
                  ? "Super admins see Lead Lists in the sidebar."
                  : "Hidden for everyone, including super admins. The page itself will not open either."}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Tools */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wrench className="h-4 w-4 text-primary" />
              {t("admin.settings.tools.title") || "System Tools"}
            </CardTitle>
            <CardDescription>
              {t("admin.settings.tools.description") || "Administrative tools for data synchronization and maintenance"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t("admin.settings.tools.syncAllCalls") || "Sync All Calls"}</Label>
              <p className="text-xs text-muted-foreground mb-2">
                {t("admin.settings.tools.syncAllCallsDesc") || "Synchronize call data from ElevenLabs and Twilio"}
              </p>
              <Button
                variant="outline"
                onClick={async () => {
                  setSyncingAllCalls(true);
                  try {
                    const res = await apiRequest("POST", "/api/admin/sync-all-calls");
                    const result = await res.json() as { total: number; success: number; failed: number; skipped: number; errors?: string[] };
                    let description = t("admin.settings.tools.syncedCalls", { success: result.success, total: result.total }) || `Synced ${result.success}/${result.total} calls`;
                    if (result.skipped > 0) {
                      description += ` (${result.skipped} skipped)`;
                    }
                    if (result.failed > 0) {
                      description += ` (${result.failed} failed)`;
                    }
                    toast({
                      title: t("admin.settings.tools.callSyncComplete") || "Call Sync Complete",
                      description,
                    });
                  } catch (error: any) {
                    toast({
                      title: t("common.error") || "Error",
                      description: error.message,
                      variant: "destructive"
                    });
                  } finally {
                    setSyncingAllCalls(false);
                  }
                }}
                disabled={syncingAllCalls}
                data-testid="button-sync-all-calls"
              >
                {syncingAllCalls ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("admin.settings.tools.syncingFromSources") || "Syncing..."}
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {t("admin.settings.tools.syncAllCalls") || "Sync All Calls"}
                  </>
                )}
              </Button>
            </div>
            
            <Separator />
            
            <div>
              <Label>{t("admin.settings.tools.syncIncomingWebhooks") || "Sync Incoming Webhooks"}</Label>
              <p className="text-xs text-muted-foreground mb-2">
                {t("admin.settings.tools.syncIncomingWebhooksDesc") || "Configure ElevenLabs webhooks for all incoming agents"}
              </p>
              <Button
                variant="outline"
                onClick={async () => {
                  setSyncingWebhooks(true);
                  try {
                    const res = await apiRequest("POST", "/api/admin/sync-incoming-webhooks");
                    const result = await res.json() as { total: number; success: number; failed: number; webhookUrl?: string; errors?: string[] };
                    toast({
                      title: t("admin.settings.tools.webhookSyncComplete") || "Webhook Sync Complete",
                      description: t("admin.settings.tools.webhooksConfigured", { success: result.success, total: result.total }) || `Configured ${result.success}/${result.total} webhooks` + (result.failed > 0 ? ` (${result.failed} failed)` : ''),
                    });
                  } catch (error: any) {
                    toast({
                      title: t("common.error") || "Error",
                      description: error.message,
                      variant: "destructive"
                    });
                  } finally {
                    setSyncingWebhooks(false);
                  }
                }}
                disabled={syncingWebhooks}
                data-testid="button-sync-webhooks"
              >
                {syncingWebhooks ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("admin.settings.tools.syncing") || "Syncing..."}
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {t("admin.settings.tools.syncIncomingWebhooks") || "Sync Incoming Webhooks"}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {hasChanges && (
        <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-in slide-in-from-bottom">
          <span className="text-sm font-medium">Unsaved changes</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleSaveAll}
            disabled={updateSettingMutation.isPending}
            data-testid="button-save-floating"
          >
            {updateSettingMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Save"
            )}
          </Button>
        </div>
      )}

      <AlertDialog open={confirmDisableMfa} onOpenChange={setConfirmDisableMfa}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn off email codes at login?</AlertDialogTitle>
            <AlertDialogDescription>
              Anyone who knows a user's password will be able to sign in without a code, including administrators.
              Only turn this off if email delivery is failing and people cannot sign in. The change applies when you click
              Save All Changes, and it is written to the security log with your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-disable-mfa">Keep it on</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setFormData(prev => ({ ...prev, login_mfa_enabled: false }));
                setHasChanges(true);
                setConfirmDisableMfa(false);
              }}
              data-testid="button-confirm-disable-mfa"
            >
              Turn off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
